import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TableCell,
  TableRow,
  IconButton,
  Chip,
  Stack,
  Alert,
  TextField,
  Grid,
  Divider,
  Link,
  Collapse,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RefreshIcon from '@mui/icons-material/Refresh';
import ClearIcon from '@mui/icons-material/Clear';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import ImageIcon from '@mui/icons-material/Image';
import api from '../config/api';
import DataTable from '../components/DataTable';

function formatDate(value) {
  if (!value) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleString();
  } catch (e) {
    return String(value);
  }
}

function normalizeCertUrls(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean).map(String);
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed.filter(Boolean).map(String);
    } catch (e) {
      /* ignore */
    }
    return value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }
  return [];
}

function isImageFile(fileName) {
  if (!fileName) return false;
  const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'];
  return imageExtensions.some((ext) => String(fileName).toLowerCase().endsWith(ext));
}

function getFileIcon(fileName) {
  if (!fileName) return <DescriptionIcon />;
  const lowerName = String(fileName).toLowerCase();
  if (lowerName.endsWith('.pdf')) return <PictureAsPdfIcon />;
  if (isImageFile(fileName)) return <ImageIcon />;
  return <DescriptionIcon />;
}

function resolveTrainerAssetUrl(url) {
  if (!url) return '';
  const s = String(url).trim();
  if (s.startsWith('http://') || s.startsWith('https://')) return s;
  const apiBase = process.env.REACT_APP_API_URL || '';
  const originBase = apiBase.replace(/\/api\/?$/, '');
  const path = s.startsWith('/') ? s : `/${s}`;
  return `${originBase || apiBase}${path}`;
}

function parseSubcategoryChips(snapshot) {
  if (!snapshot) return [];
  const raw = snapshot.subcategories;
  if (Array.isArray(raw)) {
    return raw.map((x) => (typeof x === 'object' && x !== null ? x.name || x.label : String(x))).filter(Boolean);
  }
  if (typeof raw === 'string' && raw.trim()) {
    return raw
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  }
  return [];
}

function truthyEmailVerified(v) {
  if (v === true || v === 1 || v === '1') return true;
  return false;
}

function DeletedTrainerDetailContent({ snapshot, detailMeta }) {
  const [rawOpen, setRawOpen] = useState(false);
  const s = snapshot || {};
  const certList = normalizeCertUrls(s.certificates_url_list || s.certificates_url || s.certificates);
  const subLabels = parseSubcategoryChips(s);

  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        Archived record · History ID {detailMeta?.id} · Former trainer ID {s.id ?? detailMeta?.original_trainer_id}
      </Typography>
      <Typography variant="body2">
        <strong>Deleted at:</strong> {formatDate(detailMeta?.deleted_at || s.deleted_at_utc)}
      </Typography>
      <Typography variant="body2">
        <strong>Source:</strong> {detailMeta?.deletion_source || s.requested_via || '—'}
      </Typography>

      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle2" gutterBottom>
        Profile
      </Typography>
      <Typography variant="body2">
        <strong>Name:</strong> {s.name || '—'}
      </Typography>
      <Typography variant="body2">
        <strong>Email:</strong> {s.email || '—'}
      </Typography>
      <Typography variant="body2">
        <strong>Phone:</strong> {s.phone || '—'}
      </Typography>
      <Typography variant="body2">
        <strong>Gender:</strong> {s.gender || '—'}
      </Typography>
      <Typography variant="body2">
        <strong>Service type:</strong> {s.service_type || '—'}
      </Typography>
      <Typography variant="body2">
        <strong>Category:</strong> {s.category_name || s.category || '—'}
      </Typography>
      <Typography variant="body2">
        <strong>Reference code:</strong> {s.reference_code || '—'}
      </Typography>
      <Typography variant="body2">
        <strong>Coins:</strong> {s.coins != null ? s.coins : '—'}
      </Typography>
      <Typography variant="body2">
        <strong>Status:</strong> {s.status || '—'}
      </Typography>
      <Typography variant="body2">
        <strong>Email verified:</strong> {truthyEmailVerified(s.email_verified) ? 'Yes' : 'No'}
      </Typography>
      <Typography variant="body2">
        <strong>Wallet transactions (count):</strong> {s.wallet_transaction_count != null ? s.wallet_transaction_count : '—'}
      </Typography>

      <Typography variant="body2" sx={{ mt: 1 }}>
        <strong>State / District:</strong> {[s.state_name, s.district_name || s.district].filter(Boolean).join(' · ') || '—'}
      </Typography>
      {s.city != null && s.city !== '' && (
        <Typography variant="body2">
          <strong>City:</strong> {String(s.city)}
        </Typography>
      )}
      {(s.device_type || s.device_token) && (
        <Typography variant="body2">
          <strong>Device:</strong> {[s.device_type, s.device_token ? '(token on file)' : null].filter(Boolean).join(' · ')}
        </Typography>
      )}

      <Typography variant="body2" sx={{ mt: 1 }}>
        <strong>Subcategories:</strong>
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
        {subLabels.length ? (
          subLabels.map((label, i) => <Chip key={`${label}-${i}`} label={label} size="small" />)
        ) : (
          <Typography variant="body2" color="text.secondary">
            —
          </Typography>
        )}
      </Box>

      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle2" gutterBottom>
        Attachments
      </Typography>

      <Typography variant="body2" sx={{ mt: 1 }}>
        <strong>Document</strong>
      </Typography>
      {s.document_url ? (
        <Box sx={{ mt: 0.5 }}>
          {isImageFile(s.document_url) ? (
            <Box
              component="button"
              type="button"
              onClick={() => detailMeta?.onPreviewImage?.(resolveTrainerAssetUrl(s.document_url))}
              sx={{
                p: 0,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                background: 'none',
                cursor: 'pointer',
                overflow: 'hidden',
                display: 'block',
              }}
              aria-label="Preview document image"
            >
              <Box
                component="img"
                src={resolveTrainerAssetUrl(s.document_url)}
                alt="Document"
                sx={{ display: 'block', maxHeight: 160, maxWidth: '100%', objectFit: 'contain' }}
              />
            </Box>
          ) : (
            <Link href={resolveTrainerAssetUrl(s.document_url)} target="_blank" rel="noopener noreferrer" underline="hover">
              Open document ({String(s.document_url).split('/').pop()})
            </Link>
          )}
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary">
          —
        </Typography>
      )}

      <Typography variant="body2" sx={{ mt: 2 }}>
        <strong>Certificates</strong>
      </Typography>
      {!certList.length ? (
        <Typography variant="body2" color="text.secondary">
          —
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 1 }}>
          {certList.map((certUrl, index) => {
            const fullUrl = resolveTrainerAssetUrl(certUrl);
            const fileLabel = String(certUrl).split('/').pop() || `Certificate ${index + 1}`;
            if (isImageFile(certUrl)) {
              return (
                <Box
                  key={`${certUrl}-${index}`}
                  component="button"
                  type="button"
                  onClick={() => detailMeta?.onPreviewImage?.(fullUrl)}
                  sx={{
                    p: 0,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    background: 'none',
                    cursor: 'pointer',
                    overflow: 'hidden',
                  }}
                  aria-label={`Preview certificate ${index + 1}`}
                >
                  <Box
                    component="img"
                    src={fullUrl}
                    alt={fileLabel}
                    sx={{ width: 100, height: 100, objectFit: 'cover', display: 'block' }}
                  />
                </Box>
              );
            }
            return (
              <Box key={`${certUrl}-${index}`} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {getFileIcon(certUrl)}
                <Link href={fullUrl} target="_blank" rel="noopener noreferrer" underline="hover">
                  {fileLabel}
                </Link>
              </Box>
            );
          })}
        </Box>
      )}

      <Box sx={{ mt: 2 }}>
        <Button size="small" onClick={() => setRawOpen(!rawOpen)}>
          {rawOpen ? 'Hide' : 'Show'} raw snapshot (JSON)
        </Button>
        <Collapse in={rawOpen}>
          <Box
            component="pre"
            sx={{
              p: 2,
              mt: 1,
              bgcolor: 'grey.100',
              borderRadius: 1,
              overflow: 'auto',
              maxHeight: 280,
              fontSize: 11,
              m: 0,
            }}
          >
            {JSON.stringify(s, null, 2)}
          </Box>
        </Collapse>
      </Box>
    </Box>
  );
}

function DeletedTrainers() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [viewOpen, setViewOpen] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [viewImagePreviewOpen, setViewImagePreviewOpen] = useState(false);
  const [viewImagePreviewSrc, setViewImagePreviewSrc] = useState('');

  const openViewImagePreview = (src) => {
    if (!src) return;
    setViewImagePreviewSrc(src);
    setViewImagePreviewOpen(true);
  };

  const fetchList = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const params = new URLSearchParams();
      params.set('limit', '500');
      params.set('offset', '0');
      if (filterStartDate) params.set('start_date', filterStartDate);
      if (filterEndDate) params.set('end_date', filterEndDate);
      const response = await api.get(`/admin/deleted-trainers?${params.toString()}`);
      setRows(response.data.data || []);
      setTotal(response.data.total ?? 0);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || err.message || 'Failed to load');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [filterStartDate, filterEndDate]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const handleView = async (row) => {
    setViewOpen(true);
    setViewLoading(true);
    setDetail(null);
    try {
      const response = await api.get(`/admin/deleted-trainers/${row.id}`);
      setDetail(response.data.data || null);
    } catch (err) {
      setDetail({
        error: err.response?.data?.error || err.message || 'Failed to load',
      });
    } finally {
      setViewLoading(false);
    }
  };

  const handleClearDates = () => {
    setFilterStartDate('');
    setFilterEndDate('');
  };

  const columns = [
    { id: 'id', label: 'ID' },
    { id: 'original_trainer_id', label: 'Former trainer ID' },
    { id: 'name', label: 'Name' },
    { id: 'email', label: 'Email' },
    { id: 'phone', label: 'Phone' },
    { id: 'deletion_source', label: 'Source' },
    { id: 'deleted_at', label: 'Deleted at' },
    { id: 'actions', label: 'Actions' },
  ];

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h5">Deleted trainer accounts</Typography>
        <Button startIcon={<RefreshIcon />} variant="outlined" onClick={fetchList} disabled={loading}>
          Refresh
        </Button>
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Records are saved when a trainer deletes their account from the mobile app. Filter by the date the account
        was archived (deleted).
      </Typography>

      <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Grid item xs={12} sm={4} md={3}>
          <TextField
            label="Deleted from (date)"
            type="date"
            value={filterStartDate}
            onChange={(e) => setFilterStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            size="small"
          />
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <TextField
            label="Deleted to (date)"
            type="date"
            value={filterEndDate}
            onChange={(e) => setFilterEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            size="small"
          />
        </Grid>
        <Grid item xs={12} sm={4} md={4}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ClearIcon />}
            onClick={handleClearDates}
            disabled={!filterStartDate && !filterEndDate}
          >
            Clear dates
          </Button>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="body2" color="text.secondary">
            Showing {rows.length} record{rows.length !== 1 ? 's' : ''} (total in range: {total})
          </Typography>
        </Grid>
      </Grid>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <DataTable
        columns={columns}
        data={rows}
        loading={loading}
        emptyMessage="No deleted trainer records for this filter"
        searchFields={['email', 'name', 'phone']}
        renderRow={(row) => (
          <TableRow key={row.id} hover>
            <TableCell>{row.id}</TableCell>
            <TableCell>{row.original_trainer_id}</TableCell>
            <TableCell>{row.name || '—'}</TableCell>
            <TableCell>{row.email}</TableCell>
            <TableCell>{row.phone || '—'}</TableCell>
            <TableCell>
              <Chip size="small" label={row.deletion_source || '—'} variant="outlined" />
            </TableCell>
            <TableCell>{formatDate(row.deleted_at)}</TableCell>
            <TableCell>
              <IconButton size="small" color="primary" aria-label="view snapshot" onClick={() => handleView(row)}>
                <VisibilityIcon />
              </IconButton>
            </TableCell>
          </TableRow>
        )}
      />

      <Dialog
        open={viewOpen}
        onClose={() => {
          setViewOpen(false);
          setViewImagePreviewOpen(false);
          setViewImagePreviewSrc('');
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Deleted trainer details</DialogTitle>
        <DialogContent dividers>
          {viewLoading && <Typography>Loading…</Typography>}
          {!viewLoading && detail?.error && <Alert severity="error">{detail.error}</Alert>}
          {!viewLoading && detail && !detail.error && detail.snapshot && (
            <DeletedTrainerDetailContent
              snapshot={detail.snapshot}
              detailMeta={{
                id: detail.id,
                original_trainer_id: detail.original_trainer_id,
                deleted_at: detail.deleted_at,
                deletion_source: detail.deletion_source,
                onPreviewImage: openViewImagePreview,
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setViewOpen(false);
              setViewImagePreviewOpen(false);
              setViewImagePreviewSrc('');
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={viewImagePreviewOpen}
        onClose={() => {
          setViewImagePreviewOpen(false);
          setViewImagePreviewSrc('');
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Image preview</DialogTitle>
        <DialogContent
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            bgcolor: 'grey.100',
            minHeight: 200,
          }}
        >
          {viewImagePreviewSrc ? (
            <Box
              component="img"
              src={viewImagePreviewSrc}
              alt="Attachment preview"
              sx={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
            />
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setViewImagePreviewOpen(false);
              setViewImagePreviewSrc('');
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default DeletedTrainers;
